/**
 * Planning timeline helpers. Due / target dates are drawn as POINT markers on
 * date columns; several items on one date collapse into a count cluster
 * (desktop). A Task with Start + Planned Duration and a known Work Schedule
 * additionally shows its WORK span, Start -> Expected Completion on working
 * days (TaskSpanBar); Due stays a separate deadline marker. Pure.
 */
import { PLAN_KIND, WORK_SPAN, dayIndexToISO, weekdayOf } from "./planningModel";
import { isWorkingDate, SCHEDULE_STATUS } from "../tasks/workSchedule";

/** Marker center for a date column. The Today line uses the same formula. */
export function markerLeft(dayIdx, startIdx, dayW) {
  return Math.round((dayIdx - startIdx) * dayW + dayW / 2);
}

/**
 * Existing status semantics only: completed / overdue / due today / upcoming
 * for tasks; Project / Location / Feature target dates are plain targets
 * (a project target in the past is overdue, as in the Overdue summary).
 */
export function itemStatus(node, todayIdx) {
  if (node.completed) return "done";
  if (node.overdue) return "overdue";
  if (node.kind !== PLAN_KIND.TASK) return "target";
  return node.dayIndex === todayIdx ? "today" : "upcoming";
}

const SEVERITY = ["overdue", "today", "upcoming", "done", "target"];

/** Dated items shown on a row: its own date, plus (when collapsed) every dated descendant. */
export function rowItems(node, isOpen, { include = () => true } = {}) {
  const out = [];
  const add = (n) => {
    if (n.dayIndex != null && include(n)) out.push({ key: `${n.kind}:${n.id}`, dayIndex: n.dayIndex, node: n });
  };
  add(node);
  if (!isOpen) {
    const walk = (n) => {
      (n.children || []).forEach((child) => {
        add(child);
        walk(child);
      });
    };
    walk(node);
  }
  return out;
}

/** One marker per date: { dayIndex, items, count, status, targetOnly }. */
export function clusterItems(items, todayIdx) {
  const byDay = new Map();
  items.forEach((item) => {
    const list = byDay.get(item.dayIndex) || [];
    list.push(item);
    byDay.set(item.dayIndex, list);
  });
  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([dayIndex, list]) => {
      const statuses = list.map((item) => itemStatus(item.node, todayIdx));
      const status = SEVERITY.find((s) => statuses.includes(s));
      const sorted = [...list].sort(
        (a, b) =>
          SEVERITY.indexOf(itemStatus(a.node, todayIdx)) - SEVERITY.indexOf(itemStatus(b.node, todayIdx)) ||
          String(a.node.name).localeCompare(String(b.node.name)),
      );
      return {
        key: `${dayIndex}`,
        dayIndex,
        items: sorted.map((item) => ({ ...item, status: itemStatus(item.node, todayIdx) })),
        count: list.length,
        status,
        targetOnly: statuses.every((s) => s === "target"),
      };
    });
}

/**
 * Non-working day for the Timeline shading: the org Work Schedule when known
 * (non-working weekdays + non-working dates), otherwise Sat/Sun as before.
 */
export function nonWorkingDay(idx, workSchedule) {
  if (workSchedule?.schedule && workSchedule.status !== SCHEDULE_STATUS.UNKNOWN) {
    return !isWorkingDate(dayIndexToISO(idx), workSchedule.schedule);
  }
  const wd = weekdayOf(idx);
  return wd === 0 || wd === 6;
}

/** True when some task has Start + Duration but no bar because the schedule is not loaded. */
export function spansNeedSchedule(tree) {
  let found = false;
  const walk = (n) => {
    if (found) return;
    if (n.spanStatus === WORK_SPAN.SCHEDULE_UNAVAILABLE) found = true;
    (n.children || []).forEach(walk);
  };
  tree.forEach(walk);
  return found;
}

export const SCHEDULE_UNAVAILABLE_NOTE =
  "Work bars are hidden: the organization work schedule isn’t loaded, so expected completion is unavailable. Due dates still show.";
